import cn from 'classnames'
import { ChordLib, getAllChords, GuitarLib, isChordVariant } from '@tunasong/music-lib'
import type { Chord, ChordVariant } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import { GuitarChord } from '../guitar/index.js'
import { Paper, Grid, type GridProps } from '@tunasong/ui-lib'
import styles from './chord-library.module.css'

export interface ChordLibraryProps extends Omit<GridProps, 'onChange'> {
  selected?: Chord
  scale?: Scale | null
  // If specified, only show these chords. @default all chords
  chords?: ChordVariant[]

  onChange(chord: Chord): void
}

export const ChordLibrary: FC<ChordLibraryProps> = props => {
  const { selected, chords = getAllChords(), scale, onChange, ...gridProps } = props

  const handleFocus = (focusChord: ChordVariant) => () => {
    if (!ChordLib.equals(focusChord, selected)) {
      onChange(focusChord)
    }
  }

  const handleSelect = (c: Chord) => () => {
    onChange(c)
  }
  const handleKeyDown = (ch: Chord) => (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter') {
      ev.preventDefault()
      onChange(ch)
    }
  }

  /** Focus on the active chord   */
  const [activeRef, setActiveRef] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    activeRef?.focus()
  }, [activeRef])

  const showChords = chords.filter(Boolean)

  return (
    <Grid container={true} spacing={2} {...gridProps}>
      {showChords.map(ch => {
        const fingering = GuitarLib.getFingering(ch)
        const matchChord = isChordVariant(selected) ? selected : undefined

        /** Always stated, so a toggle button reads as "not pressed" rather than not a toggle */
        const isSelected = Boolean(ChordLib.equals(ch, matchChord))
        return ch && fingering ? (
          <Grid key={ChordLib.getName(ch)}>
            {/*
             * A named button (GH #2488, P1): a screen reader said nothing about which chord a tile was. Its name
             * is spoken words, "A minor 7" rather than "Am7" read letter by letter (Bowie); the symbol stays the
             * visible label and the tooltip. The diagram is decoration.
             */}
            <Paper
              className={cn(styles.chordPaper, isSelected && styles.chordPaperSelected)}
              ref={isSelected ? setActiveRef : null}
              variant="outlined"
              role="button"
              aria-label={ChordLib.spokenChordName(ch)}
              aria-pressed={isSelected}
              title={ChordLib.getName(ch)}
              tabIndex={0}
              onClick={handleSelect(ch)}
              onFocus={handleFocus(ch)}
              onKeyDown={handleKeyDown(ch)}
            >
              <div aria-hidden="true">
                <GuitarChord chord={ch} scale={scale} />
              </div>
            </Paper>
          </Grid>
        ) : null
      })}
    </Grid>
  )
}

export default ChordLibrary
